import React from 'react';
import type { StreamChat } from 'stream-chat';
import { STREAM_IO_API_KEY } from 'config';
import { ChatUser } from 'types';

export const useChatConnect = (chatUser: ChatUser | null) => {
  const [chatClient, setChatClient] = React.useState<StreamChat | null>(null);

  React.useEffect(() => {
    if (chatUser) {
      // Under some circumstances, a "connectUser" operation might be interrupted
      // (fast user switching, react strict-mode in dev). With this flag, we control
      // whether a "disconnectUser" operation has been requested before we
      // provide a new StreamChat instance to the consumers of this hook.
      let didUserConnectInterrupt = false;
      let client: StreamChat | null = null;

      // Loaded on demand: the provider that calls this hook is mounted app-wide,
      // and the Stream client is a chunk only a page that opens a chat needs.
      const connectUser = import('stream-chat')
        .then(({ StreamChat: StreamChatClient }) => {
          client = new StreamChatClient(STREAM_IO_API_KEY, {
            enableWSFallback: true,
            timeout: 6000,
          });
          return client.connectUser(
            { id: chatUser.id, name: chatUser.name, image: chatUser.image },
            chatUser.token
          );
        })
        .catch((e) => {
          console.error(`Failed to connect user`, e);
        })
        .then(() => {
          if (!didUserConnectInterrupt && client) {
            setChatClient(client);
          }
        });

      return () => {
        didUserConnectInterrupt = true;
        // there might be a pending "connectUser" operation, wait for it to finish
        // before executing the "disconnectUser" in order to prevent race-conditions.
        connectUser.then(() => {
          setChatClient(null);
          client?.disconnectUser().catch((e) => {
            console.error(`Failed to disconnect user`, e);
          });
        });
      };
    }
  }, [chatUser]);

  return chatClient;
};
